/**
 * Supabase family service.
 *
 * Implements {@link IFamilyService} against the multi-family schema (007):
 * `family_members` is the membership source of truth; privileged flows
 * (create/join/leave/remove/role) go through SECURITY DEFINER RPCs so the
 * server enforces the family-first invariants (last-parent guards, invitation
 * validity, membership checks).
 *
 * @module features/family/services/supabaseFamilyService
 */

import { UserRole, isUserRole } from '@/features/auth/types/auth.types';
import { getSupabaseClient } from '@/shared/clients/supabaseClient';
import {
  getCurrentProfileId,
  clearFamilyContextCache,
} from '@/shared/lib/familyContext';
import { sanitizeText } from '@/shared/lib/sanitize';
import type { Database } from '@/shared/types/database';
import { generateInviteCode, normalizeInviteCode } from '../lib/inviteCode';
import type {
  CreateInvitationInput,
  Family,
  FamilyInvitation,
  FamilyMember,
  InviteMemberInput,
  InviteMemberResult,
} from '../types/family.types';
import type { IFamilyService } from './familyService.types';

type InvitationRow = Database['public']['Tables']['family_invitations']['Row'];

function toRole(value: string): UserRole {
  return isUserRole(value) ? value : UserRole.CHILD;
}

/**
 * Bounds `create_family` applies server-side (010_family_first_signup.sql:135:
 * `length(trim(p_name)) < 2 or > 80 -> raise 'invalid family name'`). They are
 * repeated here because `renameFamily` writes `families.name` through
 * PostgREST rather than through that RPC, and the column itself carries no
 * CHECK — so without this guard the two write paths disagree about what a
 * legal family name is, and only one of them is enforced at all.
 */
const FAMILY_NAME_MIN_LENGTH = 2;
const FAMILY_NAME_MAX_LENGTH = 80;

/**
 * Sanitize and bound a user-supplied family name.
 *
 * The service — not the form — is the boundary: `CreateFamilyForm` and the
 * rename dialog both validate, but a direct store or service call reaches the
 * column having touched neither, exactly the way the todo/calendar/album
 * services already assume (`sanitizeText` at their own write sites).
 *
 * Order matters. Sanitizing FIRST and measuring the result is what makes the
 * length check honest: `sanitizeText` strips markup, so a name typed as
 * `<b></b>` arrives seven characters long and leaves as an empty string —
 * only the post-sanitize length says whether the DB will accept it.
 */
function cleanFamilyName(name: string): string {
  const clean = sanitizeText(name);
  if (clean.length < FAMILY_NAME_MIN_LENGTH) {
    throw new Error(
      `Името на семейството трябва да бъде поне ${FAMILY_NAME_MIN_LENGTH} символа.`
    );
  }
  if (clean.length > FAMILY_NAME_MAX_LENGTH) {
    throw new Error('Името на семейството е твърде дълго.');
  }
  return clean;
}

function rowToInvitation(row: InvitationRow): FamilyInvitation {
  return {
    id: row.id,
    familyId: row.family_id,
    code: row.code,
    role: toRole(row.role),
    createdBy: row.created_by,
    email: row.email,
    invitedProfileId: row.invited_profile_id,
    expiresAt: row.expires_at,
    maxUses: row.max_uses,
    usedCount: row.used_count,
    revokedAt: row.revoked_at,
    createdAt: row.created_at,
  };
}

export class SupabaseFamilyService implements IFamilyService {
  private get client() {
    return getSupabaseClient();
  }

  async getMyFamilies(): Promise<readonly Family[]> {
    const profileId = await getCurrentProfileId();
    const { data, error } = await this.client
      .from('family_members')
      .select('role, created_at, families ( id, name, created_at )')
      .eq('profile_id', profileId)
      .order('created_at', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }
    return (data ?? []).flatMap((row) => {
      const family = row.families;
      if (!family) {
        return [];
      }
      return [
        {
          id: family.id,
          name: family.name,
          role: toRole(row.role),
          createdAt: family.created_at,
        },
      ];
    });
  }

  async getActiveFamilyId(): Promise<string | null> {
    const profileId = await getCurrentProfileId();
    const { data, error } = await this.client
      .from('profiles')
      .select('active_family_id')
      .eq('id', profileId)
      .single();
    if (error) {
      throw new Error(error.message);
    }
    return data.active_family_id;
  }

  async createFamily(name: string): Promise<Family> {
    const cleanName = cleanFamilyName(name);
    const { data, error } = await this.client.rpc('create_family', {
      p_name: cleanName,
    });
    if (error) {
      throw new Error(error.message);
    }
    clearFamilyContextCache();
    // The optimistic row must carry the name that was actually stored, not the
    // raw input — otherwise the switcher shows markup the column never got.
    return {
      id: data,
      name: cleanName,
      role: UserRole.SUPER_ADMIN,
      createdAt: new Date().toISOString(),
    };
  }

  async joinFamilyWithCode(code: string): Promise<Family> {
    const { data: familyId, error } = await this.client.rpc(
      'join_family_with_code',
      { p_code: normalizeInviteCode(code) }
    );
    if (error) {
      throw new Error(translateJoinError(error.message));
    }
    clearFamilyContextCache();

    const families = await this.getMyFamilies();
    const joined = families.find((family) => family.id === familyId);
    if (!joined) {
      throw new Error('Присъединяването е неуспешно: семейството липсва.');
    }
    return joined;
  }

  async setActiveFamily(familyId: string): Promise<void> {
    const { error } = await this.client.rpc('set_active_family', {
      p_family_id: familyId,
    });
    if (error) {
      throw new Error(error.message);
    }
    clearFamilyContextCache();
  }

  async leaveFamily(familyId: string): Promise<void> {
    const { error } = await this.client.rpc('leave_family', {
      p_family_id: familyId,
    });
    if (error) {
      throw new Error(translateLeaveError(error.message));
    }
    clearFamilyContextCache();
  }

  async renameFamily(familyId: string, name: string): Promise<void> {
    // Validated BEFORE the client is touched: this write goes straight to the
    // column through PostgREST, so `create_family`'s 2..80 guard never runs.
    const cleanName = cleanFamilyName(name);
    const { error } = await this.client
      .from('families')
      .update({ name: cleanName })
      .eq('id', familyId);
    if (error) {
      throw new Error(error.message);
    }
  }

  async getMembers(familyId: string): Promise<readonly FamilyMember[]> {
    const { data, error } = await this.client
      .from('family_members')
      .select(
        'id, family_id, profile_id, display_name, role, avatar_color, created_at, profiles ( username, email, avatar_url, avatar_color )'
      )
      .eq('family_id', familyId)
      .order('created_at', { ascending: true });
    if (error) {
      throw new Error(error.message);
    }
    return (data ?? []).map((row) => {
      const profile = row.profiles;
      const avatarColor = row.avatar_color ?? profile?.avatar_color ?? null;
      return {
        id: row.id,
        familyId: row.family_id,
        profileId: row.profile_id,
        displayName: row.display_name,
        role: toRole(row.role),
        joinedAt: row.created_at,
        ...(avatarColor != null && { avatarColor }),
        ...(profile?.avatar_url != null && { avatarUrl: profile.avatar_url }),
        ...(profile?.username != null && { username: profile.username }),
        ...(profile?.email != null && { email: profile.email }),
      };
    });
  }

  async removeMember(familyId: string, profileId: string): Promise<void> {
    const { error } = await this.client.rpc('remove_family_member', {
      p_family_id: familyId,
      p_profile_id: profileId,
    });
    if (error) {
      throw new Error(translateMemberError(error.message));
    }
  }

  async updateMemberRole(
    familyId: string,
    profileId: string,
    role: UserRole
  ): Promise<void> {
    const { error } = await this.client.rpc('update_family_member_role', {
      p_family_id: familyId,
      p_profile_id: profileId,
      p_role: role,
    });
    if (error) {
      throw new Error(translateMemberError(error.message));
    }
  }

  async getInvitations(familyId: string): Promise<readonly FamilyInvitation[]> {
    const { data, error } = await this.client
      .from('family_invitations')
      .select('*')
      .eq('family_id', familyId)
      .order('created_at', { ascending: false });
    if (error) {
      throw new Error(error.message);
    }
    return (data ?? []).map(rowToInvitation);
  }

  async createInvitation(
    familyId: string,
    input: CreateInvitationInput
  ): Promise<FamilyInvitation> {
    const expiresAt =
      input.expiresInDays != null && input.expiresInDays > 0
        ? new Date(
            Date.now() + input.expiresInDays * 24 * 60 * 60 * 1000
          ).toISOString()
        : null;

    // Retry once on the (astronomically unlikely) code collision.
    let lastError: Error | null = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const code = generateInviteCode();
      // `p_expires_at` and `p_max_uses` are `default null` in the RPC
      // (010_family_first_signup.sql:163-164) and "no expiry" / "unlimited
      // uses" must therefore reach Postgres as SQL NULL. The `?? ''` / `?? 0`
      // sentinels this used to send were not merely odd, they were both
      // DB-invalid: '' does not cast to timestamptz (22007, so a no-expiry
      // invitation failed outright), and `max_uses = 0` violates
      // `check (max_uses is null or max_uses > 0)` (007_multi_family.sql:46)
      // — and had it stored, `used_count >= max_uses` would hold on the very
      // first redemption, i.e. an invitation born exhausted.
      //
      // Both nulls are now passed through literally: `database.ts` types the
      // two args as `p_expires_at?: string | null` / `p_max_uses?: number |
      // null`, matching their DEFAULT NULL, so "no expiry" and "unlimited
      // uses" travel as the value they actually are instead of as an absent
      // key. `input.maxUses` is coalesced because it is an optional field and
      // `exactOptionalPropertyTypes` will not let `undefined` through a
      // property typed `number | null`.
      const { data, error } = await this.client.rpc(
        'create_family_invitation',
        {
          p_family_id: familyId,
          p_code: code,
          p_role: input.role,
          p_expires_at: expiresAt,
          p_max_uses: input.maxUses ?? null,
        }
      );
      if (!error) {
        return {
          id: data,
          familyId,
          code,
          role: input.role,
          createdBy: await getCurrentProfileId(),
          email: null,
          invitedProfileId: null,
          expiresAt,
          maxUses: input.maxUses ?? null,
          usedCount: 0,
          revokedAt: null,
          createdAt: new Date().toISOString(),
        };
      }
      lastError = new Error(translateInviteError(error.message));
      if (!error.message.includes('duplicate key')) {
        break;
      }
    }
    throw lastError ?? new Error('Създаването на покана е неуспешно.');
  }

  async inviteMember(
    familyId: string,
    input: InviteMemberInput
  ): Promise<InviteMemberResult> {
    // The Edge Function owns all server-side guards (membership, rank floor,
    // rank cap), creates the email-bound invitation and dispatches the
    // Supabase Auth invite email.
    const response = await this.client.functions.invoke<
      | {
          status: 'invited' | 'existing_user';
          invitationId: string;
          code: string;
          joinLink?: string;
        }
      | { error: string }
    >('invite-family-member', {
      body: {
        familyId,
        email: input.email.trim().toLowerCase(),
        role: input.role,
      },
    });

    if (response.error) {
      // Supabase wraps non-2xx responses; try to surface the function's
      // error payload, falling back to the generic message.
      const context = (
        response.error as { context?: { json?: () => Promise<unknown> } }
      ).context;
      if (context?.json) {
        try {
          const payload = (await context.json()) as { error?: string };
          if (payload.error) {
            throw new Error(translateInviteError(payload.error));
          }
        } catch (parseError) {
          if (parseError instanceof Error && parseError.message) {
            throw parseError;
          }
        }
      }
      throw new Error(
        response.error instanceof Error
          ? translateInviteError(response.error.message)
          : 'Изпращането на поканата е неуспешно.'
      );
    }

    const payload = response.data;
    if (!payload || 'error' in payload) {
      throw new Error(
        payload && 'error' in payload
          ? translateInviteError(payload.error)
          : 'Изпращането на поканата е неуспешно.'
      );
    }

    return {
      status: payload.status,
      invitationId: payload.invitationId,
      code: payload.code,
      ...(payload.joinLink ? { joinLink: payload.joinLink } : {}),
    };
  }

  async revokeInvitation(invitationId: string): Promise<void> {
    const { error } = await this.client
      .from('family_invitations')
      .update({ revoked_at: new Date().toISOString() })
      .eq('id', invitationId);
    if (error) {
      throw new Error(error.message);
    }
  }
}

/** Map raised Postgres exception messages to localized user-facing text. */
function translateJoinError(message: string): string {
  if (message.includes('invitation not found')) {
    return 'Невалиден код за присъединяване.';
  }
  if (message.includes('invitation revoked')) {
    return 'Тази покана е оттеглена.';
  }
  if (message.includes('invitation expired')) {
    return 'Тази покана е изтекла.';
  }
  if (message.includes('invitation exhausted')) {
    return 'Тази покана вече е използвана максималния брой пъти.';
  }
  if (message.includes('bound to a different email')) {
    return 'Тази покана е за друг имейл адрес.';
  }
  if (message.includes('already a member')) {
    return 'Вече сте член на това семейство.';
  }
  return message;
}

function translateLeaveError(message: string): string {
  if (message.includes('last super admin cannot leave')) {
    return 'Последният основател не може да напусне семейство с други членове.';
  }
  if (message.includes('not a member')) {
    return 'Не сте член на това семейство.';
  }
  return message;
}

function translateMemberError(message: string): string {
  if (message.includes('cannot remove the last super admin')) {
    return 'Последният основател в семейството не може да бъде премахнат.';
  }
  if (message.includes('cannot demote the last super admin')) {
    return 'Последният основател не може да бъде понижен.';
  }
  if (message.includes('cannot modify a member with a higher role')) {
    return 'Не можете да променяте член с по-висока роля от вашата.';
  }
  if (message.includes('cannot remove a member with a higher role')) {
    return 'Не можете да премахнете член с по-висока роля от вашата.';
  }
  if (message.includes('cannot assign a role higher than your own')) {
    return 'Не можете да зададете роля, по-висока от вашата.';
  }
  if (message.includes('only a parent')) {
    return 'Само родител може да управлява членовете на семейството.';
  }
  if (message.includes('use leave_family')) {
    return 'Използвайте „Напусни семейството“, за да премахнете себе си.';
  }
  return message;
}

/** Map invitation guard messages (RPC + Edge Function) to localized text. */
function translateInviteError(message: string): string {
  if (message.includes('cannot assign a role higher than your own')) {
    return 'Не можете да зададете роля, по-висока от вашата.';
  }
  if (message.includes('only a parent')) {
    return 'Само родител може да изпраща покани.';
  }
  if (message.includes('not a member')) {
    return 'Не сте член на това семейство.';
  }
  if (message.includes('invalid email')) {
    return 'Невалиден имейл адрес.';
  }
  if (message.includes('invalid invitation code format')) {
    return 'Невалиден формат на кода за покана.';
  }
  return message;
}
